import { ErrorCode, PortalApiError, IdentityCheckStatus } from '@afterpaytouch/portal-api/types'
import { Route } from '../../config/router'
import { TrackingEvent } from '../../model/monitoring'
import { isCountryNeedsSoftIdAttempt } from '../../model/country'
import { getIdentities, setCheckoutError, setSkipBiometricCheck } from '../../state'
import { errorsEligibleForIdVerification, handleStartCheckoutIdentityError, terminalIdvErrors } from '../../model/identity'
import { ErrorHandler, CheckoutErrorContext } from './types'

export const createCheckoutStartIdentityErrorHandler = (): ErrorHandler => {
  const identityErrorCodes = [...errorsEligibleForIdVerification, ...terminalIdvErrors]

  return {
    canHandle: (errorCode: ErrorCode, _: PortalApiError): boolean => {
      return identityErrorCodes.includes(errorCode)
    },

    handle: async (errorCode: ErrorCode, error: PortalApiError, context: CheckoutErrorContext): Promise<Route | undefined> => {
      context.logEvent(TrackingEvent.CHECKOUT_DECLINED, {
        errorCode,
        isRecoverableTerminalError: false,
      })

      // Handle retry logic for verified identities
      if (!context.hasRetriedStartCheckout) {
        const needsSoftIdAttempt = isCountryNeedsSoftIdAttempt(context.country)
        if (needsSoftIdAttempt) {
          const identitiesResult = await context.dispatch(getIdentities()).unwrap()
          if (identitiesResult?.identityCheckStatus === IdentityCheckStatus.VERIFIED) {
            context.setHasRetriedStartCheckout(true)
            return undefined
          }
        }
      }

      const needsSoftIdAttempt = isCountryNeedsSoftIdAttempt(context.country)
      let identitiesResult
      if (needsSoftIdAttempt) {
        identitiesResult = await context.dispatch(getIdentities()).unwrap()
        // Soft Id retry flow
        if (identitiesResult?.identityCheckStatus === IdentityCheckStatus.NOT_VERIFIED && identitiesResult?.identityCheckMetadata?.profileCheckAttemptsLeft) {
          context.triggerExperiment?.()
        }
      }

      const result = handleStartCheckoutIdentityError({
        errorCode,
        country: context.country,
        flags: context.flags,
        identitiesResult,
      })

      if (result?.route === Route.VERIFYING_BIOMETRIC) {
        context.dispatch(setSkipBiometricCheck(true))
      }

      if (result?.checkoutError != null) {
        context.dispatch(setCheckoutError(result.checkoutError))
      }

      return result?.route as Route
    },
  }
}
